import React from 'react';
import { Link } from 'react-router-dom';
import '../newsite/newsite.css';
import {
  HiggsfieldHero,
  SiteEffects,
  SiteHeader,
  SiteFooter,
  GrowthOSVideo,
  useReveal,
  usePageMeta,
  CALENDLY_URL,
  GROWTHOS_SIGNUP_URL,
} from '../newsite/SiteChrome';

export default function Home() {
  useReveal();
  usePageMeta(
    'X Scale — Project House for Agentic Infrastructure',
    "X Scale builds companies that don't run on manual work: systems, agents, and operating layers for AI-amplified businesses."
  );

  return (
    <div className="xscale-site">
      <SiteEffects />
      <SiteHeader />

      <main id="top">
        {/* HERO */}
        <section className="hero section-shell" data-testid="hero-section">
          <HiggsfieldHero />
          <div className="hero-orb" aria-hidden="true" />
          <div className="hero-grid" aria-hidden="true" />
          <div className="hero-inner">
            <h1>We build companies that don&rsquo;t run on manual work.</h1>
            <p className="hero-copy">
              X Scale builds the systems, agents, and operating layers that run departments,
              enhance operators, and turn traditional businesses into AI-amplified assets.
            </p>
            <div className="hero-actions">
              <a className="button button-primary" href="#projects" data-testid="hero-view-projects">View Projects</a>
              <a className="button button-secondary" href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="hero-book-call">Book a Call</a>
            </div>
          </div>
          <div className="system-strip" aria-label="Infrastructure signals">
            <div><b>01</b><span>Clean repeatable systems</span></div>
            <div><b>02</b><span>Agents that execute</span></div>
            <div><b>03</b><span>Departments that scale</span></div>
          </div>
        </section>

        {/* THESIS */}
        <section id="thesis" className="thesis section-shell reveal" data-testid="thesis-section">
          <div className="section-label">THESIS</div>
          <div className="thesis-grid">
            <h2>Most companies are installing AI on top of broken systems.</h2>
            <div className="thesis-copy">
              <p>Scale used to require more people, more managers, more meetings, and more manual execution.</p>
              <p>The next generation of scale will be built on clean, repeatable systems executed by agents that don&rsquo;t sleep.</p>
              <p>X Scale builds that infrastructure first — then turns it into projects, products, and companies.</p>
            </div>
          </div>
        </section>

        {/* WHAT WE BUILD */}
        <section className="builds section-shell reveal" data-testid="builds-section">
          <div className="section-label">WHAT WE BUILD</div>
          <div className="builds-grid">
            <article>
              <span>DEPARTMENT AGENTS</span>
              <h3>Systems that execute workflows instead of just documenting them.</h3>
              <p>Agents for growth, creative, operations, support, fulfillment, and delivery — built on the real process underneath the company.</p>
            </article>
            <article>
              <span>OPERATING LAYERS</span>
              <h3>Infrastructure that sits across teams and turns knowledge into execution.</h3>
              <p>Company-specific operating systems trained around offers, customers, procedures, decisions, and outcomes.</p>
            </article>
            <article>
              <span>AI-AMPLIFIED COMPANIES</span>
              <h3>New business assets built on top of existing leverage.</h3>
              <p>Audience, expertise, distribution, and proven operations transformed into agentic products and companies.</p>
            </article>
          </div>
        </section>

        {/* FEATURED PROJECT */}
        <section id="projects" className="projects section-shell reveal" data-testid="projects-section">
          <div className="projects-head">
            <div>
              <div className="section-label">FEATURED PROJECT</div>
              <h2>GrowthOS by Smart Marketer</h2>
            </div>
            <Link className="button button-secondary" to="/growthos" data-testid="view-case-study-btn">View Case Study</Link>
          </div>
          <div className="project-card">
            <div className="project-visual growthos-visual" aria-label="Animated GrowthOS Slack workflow demo">
              <GrowthOSVideo caption="Gary in Slack: brief in, landing page + creative + ads out." />
            </div>
            <div className="project-content">
              <span className="pill">ECOMMERCE OPERATING SYSTEM</span>
              <h3>The AI that actually does work for ecommerce brands.</h3>
              <p>
                GrowthOS is the first public proof of the X Scale model: an agentic operating system
                where brands can ask Gary in Slack to build the landing page, generate creative,
                launch ads, and ship the email flow.
              </p>
              <ul>
                <li>Turns Smart Marketer&rsquo;s ecommerce knowledge into repeatable execution.</li>
                <li>Packages agency-grade workflows into agent-driven infrastructure.</li>
                <li>Shows how traditional expertise becomes an AI-amplified company asset.</li>
              </ul>
              <div className="project-actions">
                <a className="button button-primary" href={GROWTHOS_SIGNUP_URL} target="_blank" rel="noreferrer" data-testid="growthos-signup-btn">Apply for GrowthOS</a>
                <Link className="button button-secondary" to="/growthos" data-testid="read-case-study-btn">Read Case Study</Link>
              </div>
            </div>
          </div>
        </section>

        {/* MODEL */}
        <section id="model" className="model section-shell reveal" data-testid="model-section">
          <div className="section-label">PROJECT HOUSE MODEL</div>
          <div className="model-panel">
            <h2>We build first. Partner selectively.</h2>
            <p>
              X Scale develops owned projects and collaborates with companies that already have
              leverage worth turning into infrastructure: audience, distribution, expertise,
              operational knowledge, or a traditional business ready to become AI-amplified.
            </p>
            <div className="criteria-grid">
              <span>Audience</span>
              <span>Expertise</span>
              <span>Distribution</span>
              <span>Repeatable Operations</span>
              <span>Traditional Business Model</span>
              <span>Operator Knowledge</span>
            </div>
          </div>
        </section>

        {/* CONTACT */}
        <section id="contact" className="contact section-shell reveal" data-testid="contact-section">
          <div className="contact-inner">
            <div>
              <div className="section-label">CONTACT</div>
              <h2>Have something worth turning into infrastructure?</h2>
            </div>
            <p>If you have distribution, expertise, or a traditional business ready to become agentic, start the conversation.</p>
            <a className="button button-primary" href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="contact-book-call">Book a Call</a>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
